import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import {
  Plus,
  Trash,
  PencilSimple,
  MapPin,
  Package,
  Truck,
  Clock,
  Check,
  Globe,
  FloppyDisk,
  Warning,
  X,
  MagnifyingGlass,
  Phone,
  NavigationArrow,
  Tag,
  CircleNotch,
  CheckSquare,
  Square,
  CaretUp,
  CheckCircle
} from '@phosphor-icons/react';
import { confirmAction } from '../../components/modals/ConfirmModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Town {
  id: string;
  name: string;
  countyId: string;
  county: { id: string; name: string };
}

interface ShippingRate {
  id: string;
  zoneId: string;
  name: string;
  rateType: string;
  baseRate: string | number;
  freeShippingEnabled: boolean;
  freeShippingMin: string | number | null;
  minDeliveryDays: number | null;
  maxDeliveryDays: number | null;
  isActive: boolean;
}

interface ShippingZone {
  id: string;
  storeId: string;
  name: string;
  description: string | null;
  cities: string[];
  regions: string[];
  pickupName: string | null;
  pickupAddress: string | null;
  pickupPhone: string | null;
  priority: number;
  isActive: boolean;
  rates: ShippingRate[];
}

// ─── Zone Modal ─────────────────────────────────────────────────────────

function ZoneModal({
  storeId,
  editingZone,
  onClose,
  onSuccess,
}: {
  storeId: string;
  editingZone: ShippingZone | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [search, setSearch] = useState('');
  const [zoneType, setZoneType] = useState<'towns' | 'counties'>(editingZone?.regions && editingZone.regions.length > 0 ? 'counties' : 'towns');
  const [selectedTowns, setSelectedTowns] = useState<Town[]>([]);
  const [selectedCounties, setSelectedCounties] = useState<any[]>([]);
  const [showTownDropdown, setShowTownDropdown] = useState(false);
  const [showOptionalPickup, setShowOptionalPickup] = useState(!!(editingZone?.pickupName));

  const editingRate = editingZone?.rates?.[0];
  const [isFreeShipping, setIsFreeShipping] = useState<boolean>(
    editingRate ? (editingRate.rateType === 'FREE' || Number(editingRate.baseRate) === 0) : false
  );

  const [form, setForm] = useState({
    name: editingZone?.name ?? '',
    pickupName: editingZone?.pickupName ?? '',
    pickupAddress: editingZone?.pickupAddress ?? '',
    pickupPhone: editingZone?.pickupPhone ?? '',
    rate: editingRate?.baseRate?.toString() ?? '',
    freeShippingMin: editingRate?.freeShippingMin?.toString() ?? '',
    estimatedDays: editingRate?.minDeliveryDays?.toString() ?? '3',
    priority: editingZone?.priority?.toString() ?? '0',
    isActive: editingZone?.isActive ?? true,
  });

  // Fetch all available towns
  const { data: allTowns = [], isLoading: isLoadingTowns } = useQuery<Town[]>({
    queryKey: ['storefront-towns'],
    queryFn: async () => {
      // Use the storefront locations/towns endpoint or any endpoint that provides towns with their counties
      const res: any = await apiClient.get('/storefront/locations/towns');
      return res.data?.data || res.data || res;
    },
  });

  // Fetch all available counties
  const { data: allCounties = [], isLoading: isLoadingCounties } = useQuery<any[]>({
    queryKey: ['storefront-counties'],
    queryFn: async () => {
      const res: any = await apiClient.get('/storefront/locations/counties');
      return res.data?.data || res.data || res;
    },
  });

  // Pre-fill selected towns if editing
  useEffect(() => {
    if (editingZone && allTowns.length > 0 && selectedTowns.length === 0 && editingZone.cities) {
      const editingTowns = allTowns.filter(t => editingZone.cities.includes(t.name));
      setSelectedTowns(editingTowns);
    }
  }, [editingZone, allTowns]);

  // Pre-fill selected counties if editing
  useEffect(() => {
    if (editingZone && allCounties.length > 0 && selectedCounties.length === 0 && editingZone.regions) {
      const editingCounties = allCounties.filter(c => editingZone.regions.includes(c.name));
      setSelectedCounties(editingCounties);
    }
  }, [editingZone, allCounties]);

  const filtered = allTowns.filter(
    t =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.county?.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Group by county
  const grouped = filtered.reduce<Record<string, Town[]>>((acc, town) => {
    const key = town.county?.name || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(town);
    return acc;
  }, {});

  const toggleTown = (town: Town) => {
    if (selectedTowns.some(t => t.id === town.id)) {
      setSelectedTowns(selectedTowns.filter(t => t.id !== town.id));
    } else {
      setSelectedTowns([...selectedTowns, town]);
    }
  };

  const removeTown = (townId: string) => {
    setSelectedTowns(selectedTowns.filter(t => t.id !== townId));
  };

  const toggleCounty = (county: any) => {
    if (selectedCounties.some(c => c.id === county.id)) {
      setSelectedCounties(selectedCounties.filter(c => c.id !== county.id));
    } else {
      setSelectedCounties([...selectedCounties, county]);
    }
  };

  const removeCounty = (countyId: string) => {
    setSelectedCounties(selectedCounties.filter(c => c.id !== countyId));
  };

  const filteredCounties = allCounties.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  const mutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const zonePayload = {
        name: data.name,
        cities: zoneType === 'towns' ? selectedTowns.map(t => t.name) : [],
        regions: zoneType === 'counties' ? selectedCounties.map(c => c.name) : [],
        priority: parseInt(data.priority || '0', 10),
        isActive: data.isActive,
        pickupName: data.pickupName || null,
        pickupAddress: data.pickupAddress || null,
        pickupPhone: data.pickupPhone || null,
      };

      const ratePayload = {
        name: isFreeShipping ? 'Free Shipping' : 'Standard Rate',
        rateType: isFreeShipping ? 'FREE' : 'FLAT_RATE',
        baseRate: isFreeShipping ? 0 : parseFloat(data.rate || '0'),
        freeShippingEnabled: isFreeShipping || !!data.freeShippingMin,
        freeShippingMin: isFreeShipping ? 0 : (data.freeShippingMin ? parseFloat(data.freeShippingMin) : null),
        minDeliveryDays: parseInt(data.estimatedDays || '3'),
        maxDeliveryDays: parseInt(data.estimatedDays || '3'),
        isActive: true,
      };

      if (editingZone) {
        // Update Zone
        await apiClient.put(API_ENDPOINTS.SHIPPING.ZONES.UPDATE(editingZone.id), zonePayload);
        
        // Update or Create Rate
        if (editingRate) {
          await apiClient.put(API_ENDPOINTS.SHIPPING.RATES.UPDATE(editingRate.id), ratePayload);
        } else {
          await apiClient.post(API_ENDPOINTS.SHIPPING.RATES.CREATE(editingZone.id), ratePayload);
        }
        return true;
      } else {
        // Create Zone
        const newZoneRes: any = await apiClient.post(API_ENDPOINTS.SHIPPING.ZONES.CREATE(storeId), zonePayload);
        const newZone = newZoneRes.data || newZoneRes;
        
        // Create Rate for the Zone
        await apiClient.post(API_ENDPOINTS.SHIPPING.RATES.CREATE(newZone.id), ratePayload);
        return true;
      }
    },
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (zoneType === 'towns' && selectedTowns.length === 0) return alert('Please select at least one town for this region.');
    if (zoneType === 'counties' && selectedCounties.length === 0) return alert('Please select at least one county for this region.');
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editingZone ? 'Edit Shipping Region' : 'Create Shipping Region'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Bundle multiple towns together and set a single delivery rate.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          <form id="zone-form" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Region Name */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-400">Region Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Nairobi Metropolitan, Coast Region"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full h-10 px-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
              />
            </div>

            {/* Zone Type Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-xl">
              <button
                type="button"
                onClick={() => setZoneType('towns')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${zoneType === 'towns' ? 'bg-white text-emerald-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Specific Towns
              </button>
              <button
                type="button"
                onClick={() => setZoneType('counties')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${zoneType === 'counties' ? 'bg-white text-emerald-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Entire Counties
              </button>
            </div>
            <p className="text-[11px] text-gray-500">
              {zoneType === 'towns'
                ? 'Specific-town pricing takes precedence over a county-wide price.'
                : 'This is a fallback for the selected counties. Any town with its own zone uses the town price instead.'}
            </p>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Priority
              </label>
              <input
                type="number"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full h-10 px-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
              />
              <p className="text-[11px] text-gray-400">
                Used only between equally specific zones. A town zone always overrides a county zone.
              </p>
            </div>

            {/* Locations Selector */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {zoneType === 'towns' ? 'Select Towns in Region *' : 'Select Counties in Region *'}
              </label>

              {/* Selected Towns Chips */}
              {zoneType === 'towns' && selectedTowns.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 p-3 bg-emerald/5 border border-emerald/20 rounded-xl">
                  {selectedTowns.map(town => (
                    <div key={town.id} className="flex items-center gap-1.5 px-2 py-1 bg-white border border-emerald/30 rounded-md shadow-sm text-[11px] font-medium text-emerald-900">
                      {town.name}
                      <button type="button" onClick={() => removeTown(town.id)} className="text-emerald hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Counties Chips */}
              {zoneType === 'counties' && selectedCounties.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 p-3 bg-emerald/5 border border-emerald/20 rounded-xl">
                  {selectedCounties.map(county => (
                    <div key={county.id} className="flex items-center gap-1.5 px-2 py-1 bg-white border border-emerald/30 rounded-md shadow-sm text-[11px] font-medium text-emerald-900">
                      {county.name}
                      <button type="button" onClick={() => removeCounty(county.id)} className="text-emerald hover:text-red-500">
                      <X weight="bold" className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="relative">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus-within:border-emerald focus-within:ring-2 focus-within:ring-emerald/10 transition-all">
                  <MagnifyingGlass weight="bold" className="w-4 h-4 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    placeholder={`Search and add ${zoneType}...`}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setShowTownDropdown(true); }}
                    onFocus={() => setShowTownDropdown(true)}
                    className="bg-transparent border-none outline-none text-sm flex-1 text-gray-900 placeholder-gray-400"
                  />
                  {(isLoadingTowns || isLoadingCounties) && <CircleNotch weight="bold" className="w-4 h-4 text-gray-400 animate-spin" />}
                  {showTownDropdown && (
                    <button type="button" onClick={() => setShowTownDropdown(false)} className="text-gray-400 hover:text-gray-600">
                    <CaretUp weight="bold" className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {showTownDropdown && zoneType === 'towns' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto">
                    {Object.keys(grouped).length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-400">
                        {isLoadingTowns ? 'Loading towns...' : 'No towns available'}
                      </div>
                    ) : (
                      Object.entries(grouped).map(([county, towns]) => (
                        <div key={county}>
                          <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                              {county} County
                            </p>
                          </div>
                          {towns.map(town => {
                            const isSelected = selectedTowns.some(t => t.id === town.id);
                            return (
                              <button
                                key={town.id}
                                type="button"
                                onClick={() => toggleTown(town)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${isSelected ? 'bg-emerald/10 hover:bg-emerald/20' : 'hover:bg-gray-50'}`}
                              >
                                {isSelected ? (
                                <CheckSquare weight="fill" className="w-4 h-4 text-emerald shrink-0" />
                                ) : (
                                <Square weight="regular" className="w-4 h-4 text-gray-300 shrink-0" />
                                )}
                                <span className={`text-sm font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>{town.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {showTownDropdown && zoneType === 'counties' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto">
                    {filteredCounties.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-400">
                        {isLoadingCounties ? 'Loading counties...' : 'No counties available'}
                      </div>
                    ) : (
                      <div className="py-2">
                        {filteredCounties.map(county => {
                          const isSelected = selectedCounties.some(c => c.id === county.id);
                          return (
                            <button
                              key={county.id}
                              type="button"
                              onClick={() => toggleCounty(county)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${isSelected ? 'bg-emerald/10 hover:bg-emerald/20' : 'hover:bg-gray-50'}`}
                            >
                              {isSelected ? (
                              <CheckSquare weight="fill" className="w-4 h-4 text-emerald shrink-0" />
                              ) : (
                              <Square weight="regular" className="w-4 h-4 text-gray-300 shrink-0" />
                              )}
                              <span className={`text-sm font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>{county.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Rate */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  Delivery Rate for Region
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isFreeShipping}
                    onChange={e => setIsFreeShipping(e.target.checked)}
                    className="w-4 h-4 text-emerald rounded border-gray-300 focus:ring-emerald cursor-pointer"
                  />
                  <span className="text-xs font-bold text-emerald flex items-center gap-1">
                   <CheckCircle weight="bold" className="w-3.5 h-3.5" />
                    Set as Free Shipping
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] text-gray-400 font-medium">Shipping Fee (KES) *</label>
                  {isFreeShipping ? (
                    <div className="w-full h-10 px-3.5 bg-emerald/10 border border-emerald/20 rounded-xl flex items-center gap-2 text-sm font-bold text-emerald shadow-sm">
                      <CheckCircle weight="fill" className="w-4 h-4 shrink-0" />
                      <span>Free Delivery (KES 0.00)</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">KES</span>
                      <input
                        type="number"
                        required={!isFreeShipping}
                        min="0"
                        step="0.01"
                        placeholder="500"
                        value={form.rate}
                        onChange={e => setForm({ ...form, rate: e.target.value })}
                        className="w-full h-10 pl-10 pr-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] text-gray-400 font-medium">Est. Delivery (days)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="3"
                    value={form.estimatedDays}
                    onChange={e => setForm({ ...form, estimatedDays: e.target.value })}
                    className="w-full h-10 px-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                  />
                </div>
              </div>

              {!isFreeShipping && (
                <div className="space-y-1.5">
                  <label className="text-[11px] text-gray-400 font-medium">
                    Free shipping if order ≥ (KES) — leave blank to disable
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">KES</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g. 5000 (optional)"
                      value={form.freeShippingMin}
                      onChange={e => setForm({ ...form, freeShippingMin: e.target.value })}
                      className="w-full h-10 pl-10 pr-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Optional Pickup Location */}
            <div className="pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowOptionalPickup(!showOptionalPickup)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald hover:text-emerald/80 transition-colors"
              >
                {showOptionalPickup ? <CaretUp weight="bold" className="w-4 h-4" /> : <CaretUp weight="bold" className="w-4 h-4 rotate-180" />}
                Add Custom Pickup Point (Optional)
              </button>
              
              {showOptionalPickup && (
                <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[11px] text-gray-500">
                    If provided, customers will see this custom pickup point for this region instead of selecting from Dukene hubs.
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-[11px] text-gray-400 font-medium">Location Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Mama Njeri's Shop"
                        value={form.pickupName}
                        onChange={e => setForm({ ...form, pickupName: e.target.value })}
                        className="w-full h-10 px-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[11px] text-gray-400 font-medium">Street Address</label>
                      <input
                        type="text"
                        placeholder="e.g. Moi Ave, Block B"
                        value={form.pickupAddress}
                        onChange={e => setForm({ ...form, pickupAddress: e.target.value })}
                        className="w-full h-10 px-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[11px] text-gray-400 font-medium">Contact Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="tel"
                          placeholder="+254 7XX XXX XXX"
                          value={form.pickupPhone}
                          onChange={e => setForm({ ...form, pickupPhone: e.target.value })}
                          className="w-full h-10 pl-9 pr-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald/50 focus:ring-2 focus:ring-emerald/10 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mt-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Active Region</p>
                <p className="text-xs text-gray-400">Customers in these towns can checkout</p>
              </div>
              <div
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.isActive ? 'bg-emerald' : 'bg-gray-200'}`}
                onClick={() => setForm({ ...form, isActive: !form.isActive })}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${form.isActive ? 'translate-x-5' : ''}`} />
              </div>
            </div>

            {mutation.isError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
                <Warning weight="fill" className="w-4 h-4 shrink-0" />
                {(mutation.error as any)?.response?.data?.error ||
                  (mutation.error as any)?.message ||
                  'Failed to save region. Please try again.'}
              </div>
            )}

          </form>
        </div>

        {/* Actions Footer */}
        <div className="p-6 border-t border-gray-100 bg-white rounded-b-2xl shrink-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 h-10 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="zone-form"
              disabled={mutation.isPending || (zoneType === 'towns' ? selectedTowns.length === 0 : selectedCounties.length === 0)}
              className="flex items-center gap-2 px-6 h-10 bg-emerald text-white rounded-xl text-sm font-medium hover:bg-emerald/90 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? (
                <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
              ) : (
                <FloppyDisk weight="bold" className="w-4 h-4" />
              )}
              {editingZone ? 'Save Changes' : 'Create Region'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Zone Card ──────────────────────────────────────────────────────────

function ZoneCard({
  zone,
  allTowns,
  onEdit,
  onDelete,
}: {
  zone: ShippingZone;
  allTowns: any[];
  onEdit: (z: ShippingZone) => void;
  onDelete: (id: string) => void;
}) {
  const rate = zone.rates?.[0];
  const isFree = rate ? (Number(rate.baseRate) === 0 || rate.rateType === 'FREE') : true;

  // Calculate towns included. For legacy county zones, cities might be empty but regions has counties.
  let townsCount = zone.cities?.length || 0;
  if (townsCount === 0 && zone.regions?.length > 0) {
    townsCount = allTowns.filter((t) => zone.regions.includes(t.county?.name) || zone.regions.includes(t.countyId)).length;
  }

  return (
    <div
      className={`group relative bg-white dark:bg-slate-900 border rounded-xl p-5 transition-all shadow-sm ${
        zone.isActive ? 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#202223]' : 'border-dashed border-[#D2D5D9] opacity-60'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#F1F2F4] dark:bg-slate-800 rounded-lg flex items-center justify-center shrink-0">
            <NavigationArrow weight="fill" className="w-4 h-4 text-[#202223] dark:text-white" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#202223] dark:text-white">{zone.name}</h4>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{townsCount} towns included</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {!zone.isActive && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62] bg-[#F1F2F4] px-2 py-0.5 rounded mr-1">
              Inactive
            </span>
          )}
          <button
            type="button"
            onClick={() => onEdit(zone)}
            className="p-1.5 text-[#5C5F62] hover:text-[#202223] dark:hover:text-white rounded-md transition-colors"
            title="Edit zone"
          >
            <PencilSimple weight="bold" className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(zone.id)}
            className="p-1.5 text-[#5C5F62] hover:text-red-600 rounded-md transition-colors"
            title="Delete zone"
          >
            <Trash weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Towns preview */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1">
          {zone.cities?.slice(0, 4).map((city, idx) => (
            <span key={idx} className="px-2 py-0.5 bg-[#F1F2F4] dark:bg-slate-800 text-[#202223] dark:text-slate-300 rounded text-[11px] font-medium">
              {city}
            </span>
          ))}
          {(zone.cities?.length || 0) > 4 && (
            <span className="px-2 py-0.5 bg-[#E1E4E8] dark:bg-slate-700 text-[#5C5F62] dark:text-slate-300 rounded text-[11px] font-medium">
              +{(zone.cities?.length || 0) - 4} more
            </span>
          )}
        </div>
      </div>

      {/* Optional Custom Pickup point */}
      {zone.pickupName && (
        <div className="mb-3 p-3 bg-[#F1F2F4] dark:bg-slate-800/80 rounded-lg border border-[#D2D5D9] dark:border-slate-700">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#202223] dark:text-white mb-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Custom pickup location
          </p>
          <p className="text-xs font-bold text-[#202223] dark:text-white">{zone.pickupName}</p>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{zone.pickupAddress}</p>
          {zone.pickupPhone && (
            <div className="flex items-center gap-1 mt-1 text-xs text-[#5C5F62] dark:text-slate-400">
              <Phone className="w-3 h-3" />
              <span>{zone.pickupPhone}</span>
            </div>
          )}
        </div>
      )}

      {/* Rate pills */}
      {rate && (
        <div className="flex flex-wrap items-center gap-1.5 pt-3 border-t border-[#E1E4E8] dark:border-slate-800">
          <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#202223] dark:bg-white text-white dark:text-slate-900">
            <Tag weight="bold" className="w-3 h-3" />
            {isFree ? 'Free shipping' : `KES ${Number(rate.baseRate).toLocaleString()}`}
          </div>

          {!isFree && rate.freeShippingMin && Number(rate.freeShippingMin) > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 text-[11px] font-semibold">
              <Check className="w-3 h-3" />
              Free over KES {Number(rate.freeShippingMin).toLocaleString()}
            </div>
          )}

          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#F1F2F4] dark:bg-slate-800 text-[#5C5F62] dark:text-slate-400 text-[11px] font-medium">
            <Clock className="w-3 h-3" />
            {rate.minDeliveryDays} day{rate.minDeliveryDays !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ShippingSettings() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state: any) => state.user);
  const activeStoreId = useAuthStore((state: any) => state.activeStoreId);
  const storeId = activeStoreId || user?.storeId || '';

  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null);
  const [isEditingGlobal, setIsEditingGlobal] = useState(false);
  const [editedSettings, setEditedSettings] = useState<any>({});
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);

  // Fetch store data
  const { data: store } = useQuery<any>({
    queryKey: ['merchant-store', storeId],
    queryFn: () => apiClient.get<any>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!storeId,
  });

  // Fetch all towns for calculating town count in legacy county zones
  const { data: allTowns = [] } = useQuery<any[]>({
    queryKey: ['storefront-towns'],
    queryFn: () => apiClient.get<any[]>(API_ENDPOINTS.STOREFRONT_LOCATIONS.TOWNS),
  });

  useEffect(() => {
    if (store?.settings?.originLocationId) {
      setSelectedHubId(store.settings.originLocationId);
    }
  }, [store]);

  // Fetch available pickup/dropoff points (for Logistics Hub)
  const { data: pickupPoints = [], isLoading: _isLoadingHubs } = useQuery<any[]>({
    queryKey: ['pickup-points', storeId],
    queryFn: () => apiClient.get<any[]>(API_ENDPOINTS.STOREFRONT_LOCATIONS.PICKUP_POINTS),
    enabled: !!storeId,
  });

  // Fetch Shipping Zones
  const { data: shippingZones = [], isLoading: isLoadingZones } = useQuery<ShippingZone[]>({
    queryKey: ['shipping-zones', storeId],
    queryFn: async () => {
      const res: any = await apiClient.get<ShippingZone[]>(API_ENDPOINTS.SHIPPING.ZONES.LIST(storeId));
      return res.data || res;
    },
    enabled: !!storeId,
  });

  // Fetch providers
  const { data: providers = [], refetch: refetchProviders } = useQuery<any[]>({
    queryKey: ['shipping-providers', storeId],
    queryFn: () => apiClient.get<any[]>(API_ENDPOINTS.SHIPPING.PROVIDERS.LIST(storeId)),
    enabled: !!storeId,
  });

  const [jumiaCreds, setJumiaCreds] = useState({ sellerId: '', apiKey: '' });

  const updateProviderMutation = useMutation({
    mutationFn: async ({ providerId, enabled, credentials }: { providerId: string; enabled: boolean; credentials?: any }) => {
      const payload: any = { enabled };
      if (credentials) payload.credentials = credentials;
      return apiClient.put(API_ENDPOINTS.SHIPPING.PROVIDERS.UPDATE(storeId, providerId), payload);
    },
    onSuccess: () => refetchProviders(),
  });

  const testProviderMutation = useMutation({
    mutationFn: async (providerId: string) => {
      const res: any = await apiClient.post(API_ENDPOINTS.SHIPPING.PROVIDERS.TEST(storeId, providerId));
      return res.data;
    },
    onSuccess: (data: any) => alert(data.message || 'Connected successfully'),
    onError: (err: any) => alert(err.response?.data?.message || err.message),
  });

  // Update store mutation
  const updateStoreMutation = useMutation({
    mutationFn: (data: any) => apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', storeId] });
      setIsEditingGlobal(false);
    },
  });

  const updateGlobalSetting = (key: string, value: any) => {
    setEditedSettings({ ...editedSettings, [key]: value });
    if (!isEditingGlobal) setIsEditingGlobal(true);
  };

  const handleGlobalSave = () => updateStoreMutation.mutate({ settings: editedSettings });

  const currentSettings = { ...(store as any)?.settings, ...editedSettings };

  // Delete Zone
  const deleteZoneMutation = useMutation({
    mutationFn: (zoneId: string) => apiClient.delete(API_ENDPOINTS.SHIPPING.ZONES.DELETE(zoneId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-zones', storeId] });
    },
  });

  const handleDeleteZone = async (zoneId: string) => {
    const confirmed = await confirmAction({
      title: 'Remove Shipping Region',
      message: 'Remove this shipping region? All associated rates will also be removed.',
      confirmText: 'Remove Region',
      cancelText: 'Cancel',
    });
    if (confirmed) {
      deleteZoneMutation.mutate(zoneId);
    }
  };

  return (
    <div className="space-y-6 pb-20 max-w-4xl">
      {/* Save Bar */}
      {isEditingGlobal && (
        <div className="sticky top-4 z-50 flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-3xl shadow-2xl animate-in slide-in-from-top-4 duration-300 gap-3">
          <div className="flex items-center gap-3 ml-1">
            <div className="w-2 h-2 rounded-full bg-emerald animate-pulse shrink-0" />
            <div>
              <p className="font-medium text-white text-sm">Unsaved Changes</p>
              <p className="text-[11px] text-gray-400">Updates are pending publication</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={() => {
                setEditedSettings({});
                setIsEditingGlobal(false);
                if (store?.settings?.originLocationId) setSelectedHubId(store.settings.originLocationId);
                else setSelectedHubId(null);
              }}
              className="px-4 h-9 text-xs font-medium text-gray-300 hover:text-white transition-colors"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleGlobalSave}
              disabled={updateStoreMutation.isPending}
              className="flex items-center justify-center gap-2 px-5 h-9 bg-white text-gray-900 rounded-lg hover:bg-white/90 transition-all font-medium text-xs md:text-sm disabled:opacity-50 flex-1 sm:flex-initial"
            >
              <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
              {updateStoreMutation.isPending ? 'Saving...' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <h1 className="text-xl font-bold text-[#202223] dark:text-white">Shipping and delivery</h1>
        </div>
      </div>

      {/* Logistics Hub Section */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#E1E4E8] dark:border-slate-800">
          <div>
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">Logistics & fulfillment origin</h2>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold self-start sm:self-auto ${
              selectedHubId
                ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300'
                : 'bg-[#F1F2F4] dark:bg-slate-800 text-[#5C5F62] dark:text-slate-300'
            }`}
          >
            {selectedHubId ? 'Network origin active' : 'Custom merchant shipping'}
          </span>
        </div>

        <div>
          <label className="block text-xs font-bold text-[#202223] dark:text-white mb-1.5">
            Primary fulfillment origin point
          </label>
          <select
            value={selectedHubId || ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedHubId(val);
              updateGlobalSetting('originLocationId', val);
            }}
            className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs font-medium text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
          >
            <option value="">Manual shipping (No hub association)</option>
            {pickupPoints.map((point: any) => (
              <option key={point.id} value={point.id}>
                {point.name} — {point.address} ({point.market?.name || point.town?.name})
              </option>
            ))}
          </select>
        </div>

        {selectedHubId ? (
          <div className="flex items-start gap-2.5 p-3.5 bg-emerald-50/70 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800/60">
            <Check className="w-4 h-4 text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-emerald-900 dark:text-emerald-200">Network hub dispatch active</p>
              <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-0.5">
                Regional dispatch will automatically route through your selected origin facility.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3.5 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
            <p className="text-xs text-[#5C5F62] dark:text-slate-400">
              Orders will rely exclusively on the regional shipping rates defined below.
            </p>
          </div>
        )}
      </div>

      {/* Global Configuration Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-4">
          <div className="pb-3 border-b border-[#E1E4E8] dark:border-slate-800">
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Fallback delivery rates</h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-[#202223] dark:text-white mb-1">
                Standard flat shipping fee (KES)
              </label>
              <input
                type="number"
                value={currentSettings.shippingFee || 0}
                onChange={(e) => updateGlobalSetting('shippingFee', parseFloat(e.target.value) || 0)}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#202223] dark:text-white mb-1">
                Free shipping threshold (KES)
              </label>
              <input
                type="number"
                value={currentSettings.freeShippingThreshold || 0}
                onChange={(e) => updateGlobalSetting('freeShippingThreshold', parseFloat(e.target.value) || 0)}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-4">
          <div className="pb-3 border-b border-[#E1E4E8] dark:border-slate-800">
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Checkout requirements</h3>
          </div>

          <div className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
            {[
              {
                key: 'requiresShippingAddress',
                title: 'Require shipping address',
                description: 'Collect physical delivery address during customer checkout',
              },
              {
                key: 'enableInventoryTracking',
                title: 'Inventory tracking',
                description: 'Check real-time stock levels prior to checkout dispatch',
              },
              {
                key: 'allowBackorders',
                title: 'Allow backorders',
                description: 'Permit customer orders when stock levels reach zero',
              },
            ].map((item) => {
              const isChecked = currentSettings[item.key as keyof typeof currentSettings] ?? false;
              return (
                <div
                  key={item.key}
                  onClick={() => updateGlobalSetting(item.key, !isChecked)}
                  className="py-3 flex items-center justify-between cursor-pointer group"
                >
                  <div>
                    <p className="text-xs font-semibold text-[#202223] dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                      {item.title}
                    </p>
                    <p className="text-[11px] text-[#5C5F62] dark:text-slate-400">
                      {item.description}
                    </p>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors shrink-0 ${
                      isChecked
                        ? 'bg-[#202223] dark:bg-white border-[#202223] dark:border-white text-white dark:text-slate-900'
                        : 'border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800'
                    }`}
                  >
                    {isChecked && <Check className="w-3.5 h-3.5 stroke-[2.5]" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Shipping Regions (Zones) */}
      <div className="space-y-4 pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">Shipping regions and zones</h2>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingZone(null);
              setShowZoneModal(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#202223] dark:bg-white text-white dark:text-slate-900 font-medium text-xs rounded-lg shadow-sm hover:opacity-90 transition-all self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Add region</span>
          </button>
        </div>

        {isLoadingZones ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-44 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : shippingZones.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 bg-white border border-dashed border-gray-200 rounded-3xl">
            <div className="w-16 h-16 bg-emerald/5 rounded-2xl flex items-center justify-center">
              <MapPin className="w-8 h-8 text-emerald/40" />
            </div>
            <div className="text-center">
              <p className="text-gray-700 font-semibold text-sm">No shipping regions configured yet</p>
            </div>
            <button
              onClick={() => { setEditingZone(null); setShowZoneModal(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald text-white rounded-xl font-medium text-sm hover:bg-emerald/90 transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Create Your First Region
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shippingZones.map(zone => (
              <ZoneCard
                key={zone.id}
                zone={zone}
                allTowns={allTowns}
                onEdit={z => { setEditingZone(z); setShowZoneModal(true); }}
                onDelete={handleDeleteZone}
              />
            ))}
          </div>
        )}
      </div>

      {/* Shipping Providers */}
      <div className="pt-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-medium text-gray-900">External Shipping Providers</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dukene Provider */}
          <div className="bg-white border text-left flex flex-col items-start border-emerald/20 rounded-2xl p-6 relative overflow-hidden shadow-sm">
            <div className="absolute top-6 right-6">
              <button
                onClick={() => {
                  const p = providers.find(p => p.providerId === 'dukene');
                  updateProviderMutation.mutate({ providerId: 'dukene', enabled: !(p?.enabled) });
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${providers.find(p => p.providerId === 'dukene')?.enabled ? 'bg-emerald' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${providers.find(p => p.providerId === 'dukene')?.enabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <div className="w-12 h-12 bg-emerald/10 text-emerald rounded-xl flex items-center justify-center mb-4 mt-[-24px]">
              <Truck className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Dukene Network</h3>
            <p className="text-sm text-gray-500 mt-1 mb-4 flex-1">
              Fully integrated proprietary delivery network. Evaluates locations to dispatch Dukene-certified riders dynamically. (Always enabled via Hub)
            </p>
            <div className="w-full flex items-center justify-between border-t border-gray-100 pt-4">
              <span className="text-xs font-semibold text-emerald bg-emerald/10 px-2 py-1 rounded-md">Active Connection</span>
            </div>
          </div>

          {/* Jumia Provider */}
          <div className="bg-white border text-left flex flex-col items-start border-gray-200 rounded-2xl p-6 relative overflow-hidden hover:shadow-md transition-all">
            <div className="absolute top-6 right-6">
              <button
                onClick={() => {
                  const p = providers.find(p => p.providerId === 'jumia');
                  updateProviderMutation.mutate({ providerId: 'jumia', enabled: !(p?.enabled) });
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${providers.find(p => p.providerId === 'jumia')?.enabled ? 'bg-emerald' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${providers.find(p => p.providerId === 'jumia')?.enabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <div className="w-12 h-12 bg-orange-100 text-orange-500 rounded-xl flex items-center justify-center mb-4 mt-[-24px]">
              <Package className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Jumia Logistics</h3>
            <p className="text-sm text-gray-500 mt-1 mb-4 flex-1">
              Connect your Jumia Seller Center account to fetch live rates and create drop-offs automatically.
            </p>
            <div className="w-full mt-4 mb-4 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Seller ID</label>
                <input 
                  type="text" 
                  placeholder={providers.find(p => p.providerId === 'jumia')?.configured ? '••••••••' : 'e.g. KE-XXXXXX'} 
                  value={jumiaCreds.sellerId}
                  onChange={e => setJumiaCreds(c => ({...c, sellerId: e.target.value}))}
                  className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-emerald/50 focus:ring-1 focus:ring-emerald outline-none transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">API Key</label>
                <input 
                  type="password" 
                  placeholder={providers.find(p => p.providerId === 'jumia')?.configured ? '••••••••••••••••' : 'Your API Key'} 
                  value={jumiaCreds.apiKey}
                  onChange={e => setJumiaCreds(c => ({...c, apiKey: e.target.value}))}
                  className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-emerald/50 focus:ring-1 focus:ring-emerald outline-none transition-all" 
                />
              </div>
            </div>
            <div className="w-full flex items-center justify-between border-t border-gray-100 pt-4">
              {providers.find(p => p.providerId === 'jumia')?.configured && (
                 <span className="text-xs font-semibold text-green-600">Configured</span>
              )}
              <button 
                onClick={() => {
                  updateProviderMutation.mutate({
                    providerId: 'jumia',
                    enabled: providers.find(p => p.providerId === 'jumia')?.enabled || false,
                    credentials: jumiaCreds.sellerId && jumiaCreds.apiKey ? jumiaCreds : undefined
                  }, {
                    onSuccess: () => {
                      testProviderMutation.mutate('jumia');
                      setJumiaCreds({ sellerId: '', apiKey: '' });
                    }
                  });
                }}
                disabled={updateProviderMutation.isPending || testProviderMutation.isPending}
                className="ml-auto text-xs font-medium text-emerald border border-emerald/20 bg-emerald/5 px-3 py-1.5 rounded hover:bg-emerald/10 transition-colors disabled:opacity-50"
              >
                {testProviderMutation.isPending ? 'Testing...' : 'Save & Test Connection'}
              </button>
            </div>
          </div>

          {/* Parcelgrid */}
          <div className="bg-gray-50 border text-left flex flex-col items-start border-gray-200 rounded-2xl p-6 relative overflow-hidden">
            <div className="w-12 h-12 bg-blue-100 text-blue-500 rounded-xl flex items-center justify-center mb-4 opacity-50">
              <Globe className="w-6 h-6" />
            </div>
            <span className="absolute top-6 right-6 text-[10px] uppercase tracking-wider font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Coming Soon</span>
            <h3 className="text-lg font-semibold text-gray-900 opacity-50">Parcelgrid</h3>
            <p className="text-sm text-gray-500 mt-1 mb-4 flex-1 opacity-50">
              Cross-border and multi-carrier aggregator. One connection, 100+ carriers worldwide.
            </p>
          </div>
        </div>
      </div>

      {/* Sticky bottom save bar */}
      {isEditingGlobal && (
        <div className="sticky bottom-4 z-50 flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-300 gap-3">
          <div className="flex items-center gap-3 ml-1">
            <div className="w-2 h-2 rounded-full bg-emerald animate-pulse shrink-0" />
            <div>
              <p className="font-medium text-white text-sm">Unsaved Delivery Preferences</p>
              <p className="text-[11px] text-gray-400">Global changes will affect checkout calculation</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button onClick={() => { setEditedSettings({}); setIsEditingGlobal(false); }} className="px-4 h-9 text-xs font-medium text-gray-300 hover:text-white transition-colors">Discard</button>
            <button onClick={handleGlobalSave} disabled={updateStoreMutation.isPending} className="flex items-center justify-center gap-2 px-5 h-9 bg-white text-gray-900 rounded-lg hover:bg-white/90 font-medium text-xs md:text-sm disabled:opacity-50 flex-1 sm:flex-initial">
              <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
              {updateStoreMutation.isPending ? 'Saving...' : 'Publish Preferences'}
            </button>
          </div>
        </div>
      )}

      {/* Zone Modal */}
      {showZoneModal && (
        <ZoneModal
          storeId={storeId}
          editingZone={editingZone}
          onClose={() => { setShowZoneModal(false); setEditingZone(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['shipping-zones', storeId] });
            setShowZoneModal(false);
            setEditingZone(null);
          }}
        />
      )}
    </div>
  );
}
